import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Divider,
  Collapse,
  TablePagination,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Autocomplete, // Added Autocomplete here
} from "@mui/material";
import {
  emailAPI,
  tasksAPI,
  calendarAPI,
  usersAPI,
} from "../../../services/api";
import TaskDialog from "../../../components/TodoList/TaskDialog";
import EventDialog from "../../../components/Calendar/EventDialog";
import DeleteIcon from "@mui/icons-material/Delete";

import { useLocation } from "react-router-dom";

const translations = {
  Minutes: "Minuten",
  Hourly: "Stündlich",
  Daily: "Täglich",
  Weekly: "Wöchentlich",
  Monthly: "Monatlich",
  Yearly: "Jährlich",
  Custom: "Benutzerdefiniert",
  Monday: "Montag",
  Tuesday: "Dienstag",
  Wednesday: "Mittwoch",
  Thursday: "Donnerstag",
  Friday: "Freitag",
  Saturday: "Samstag",
  Sunday: "Sonntag",
  Expression: "Ausdruck",
  Day: "Tag",
  hour: "Stunde",
  Every: "Alle",
  At: "Um",
  on: "am",
  First: "Ersten",
  Second: "Zweiten",
  Third: "Dritten",
  Fourth: "Vierten",
  Last: "Letzten",
  "of every month(s)": "jedes Monat(e)",
  "Last day of every month": "Letzter Tag jedes Monats",
  "On the last weekday of every month": "Am letzten Wochentag jedes Monats",
  "day(s) before the end of the month": "Tag(e) vor dem Ende des Monats",
  "Days of every month": "Tag(e) vor dem Ende des Monats",
  "On the": "Am",
  "weekday of every month": "Wochentag jedes Monats",
  "weekend day of every month": "Wochenendtag jedes Monats",
  "Every week": "Jede Woche",
  "Every week day": "Jeden Wochentag",
  "minute(s)": "Minute(n)",
  "hour(s)": "Stunde(n)",
  "day(s)": "Tag(e)",
  "week(s)": "Woche(n)",
  "month(s)": "Monat(e)",
  "Start time": "Startzeit",
};

const translateFn = (key) => {
  return translations[key] || key;
};

const EmailTab = () => {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cronStatus, setCronStatus] = useState(null);
  const [markAsSeen, setMarkAsSeen] = useState(true);
  const [cronValue, setCronValue] = useState("*/5 * * * *");

  // Pagination & Search State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchSubject, setSearchSubject] = useState("");
  const [searchSender, setSearchSender] = useState("");

  // Dialog States
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogData, setTaskDialogData] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDialogData, setEventDialogData] = useState(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetEmailId, setDeleteTargetEmailId] = useState(null);

  // Assign Task Dialog State
  const [assignTaskDialogOpen, setAssignTaskDialogOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskToAssign, setSelectedTaskToAssign] = useState(null);

  // Data for Dialogs
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [availableUsernames, setAvailableUsernames] = useState([]);

  const location = useLocation();

  // Read URL params
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const searchId = query.get("id");
    if (searchId) {
      setSearchSubject(`ID:${searchId}`); // Visual indicator
      loadEmails(searchId);
    } else {
      loadEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, page, rowsPerPage]);

  const loadUsersAndCurrentUser = async () => {
    try {
      const usersResponse = await usersAPI.listUsers();
      setUsers(usersResponse.data);
      setAvailableUsernames(
        usersResponse.data.map((u) => u.username).filter(Boolean),
      );

      const meResponse = await usersAPI.readUser("me");
      setCurrentUser(meResponse.data);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  // Modified loadEmails to accept optional direct ID
  const loadEmails = async (specificId = null) => {
    try {
      setLoading(true);
      const params = {
        page: page + 1, // Backend is 1-indexed
        limit: rowsPerPage,
        title:
          searchSubject && !searchSubject.startsWith("ID:")
            ? searchSubject
            : undefined,
        sender: searchSender || undefined,
        id: specificId || undefined,
      };

      const response = await emailAPI.getEmailMessages(params);

      // If searching by specific ID, auto-select the first result if found
      if (
        specificId &&
        response.data &&
        (response.data.items?.length > 0 || response.data.length > 0)
      ) {
        const foundEmail = response.data.items
          ? response.data.items[0]
          : response.data[0];
        setSelectedEmail(foundEmail);
      }

      if (response.data && Array.isArray(response.data.items)) {
        setEmails(response.data.items);
        setTotalCount(response.data.total);

        // Store in localStorage if we are on the first page and not searching
        if (page === 0 && !searchSubject && !searchSender && !specificId) {
          try {
            localStorage.setItem("emails", JSON.stringify(response.data.items));
            localStorage.setItem(
              "emails_total",
              response.data.total.toString(),
            );
            localStorage.setItem("emails_timestamp", Date.now().toString());
          } catch (e) {
            console.warn("Failed to store emails in localStorage", e);
          }
        }
      } else if (Array.isArray(response.data)) {
        // Fallback for old API response structure if needed
        setEmails(response.data);
        setTotalCount(response.data.length);

        if (page === 0 && !searchSubject && !searchSender && !specificId) {
          try {
            localStorage.setItem("emails", JSON.stringify(response.data));
            localStorage.setItem(
              "emails_total",
              response.data.length.toString(),
            );
            localStorage.setItem("emails_timestamp", Date.now().toString());
          } catch (e) {
            console.warn("Failed to store emails in localStorage", e);
          }
        }
      } else {
        setEmails([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to load emails:", err);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load cached emails first to show something immediately
    const cachedEmails = localStorage.getItem("emails");
    if (cachedEmails) {
      try {
        setEmails(JSON.parse(cachedEmails));
        const cachedTotal = localStorage.getItem("emails_total");
        if (cachedTotal) setTotalCount(parseInt(cachedTotal, 10));
      } catch (e) {
        console.warn("Failed to parse cached emails", e);
      }
    }

    // Load cached tasks or fetch them
    loadTasks();

    // Initial load handled by location effect
    loadCronStatus();
    loadUsersAndCurrentUser();
  }, []);

  const loadTasks = async () => {
    // Check localStorage first
    const cachedTasks = localStorage.getItem("tasks");
    if (cachedTasks) {
      try {
        setTasks(JSON.parse(cachedTasks));
      } catch (e) {
        console.warn("Failed to parse local tasks", e);
      }
    }

    try {
      // Fetch fresh tasks
      const response = await tasksAPI.getTasks();
      const loadedTasks = Array.isArray(response.data)
        ? response.data
        : response.data.items || [];
      setTasks(loadedTasks);
      localStorage.setItem("tasks", JSON.stringify(loadedTasks));
    } catch (err) {
      console.error("Failed to load tasks:", err);
    }
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page on new search
    loadEmails();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchEmails = async () => {
    try {
      setLoading(true);
      await emailAPI.fetchEmails(markAsSeen);
      loadEmails(); // Reload after fetching
    } catch (err) {
      console.error("Failed to fetch emails:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCronStatus = async () => {
    try {
      const response = await emailAPI.getEmailFetchCronStatus();
      setCronStatus(response.data);
    } catch (err) {
      console.error("Failed to load cron status:", err);
    }
  };

  const startCron = async () => {
    try {
      // Ensure strictly 5-part UNIX cron format: Minute Hour Day Month DayOfWeek
      // react-cron-generator often produces 7-part Quartz format: Seconds Minutes Hours DayOfMonth Month DayOfWeek Year
      const parts = cronValue.trim().split(/\s+/);
      let unixCron = cronValue;

      if (parts.length === 6 || parts.length === 7) {
        // Assume format: Seconds Minutes Hours DayOfMonth Month DayOfWeek [Year]
        // We want: Minutes Hours DayOfMonth Month DayOfWeek
        unixCron = parts.slice(1, 6).join(" ");
      }

      // Replace Quartz specific '?' with '*'
      unixCron = unixCron.replace(/\?/g, "*");

      await emailAPI.startEmailFetchCron(unixCron);
      loadCronStatus();
    } catch (err) {
      console.error("Failed to start cron:", err);
    }
  };

  const stopCron = async () => {
    try {
      await emailAPI.stopEmailFetchCron();
      loadCronStatus();
    } catch (err) {
      console.error("Failed to stop cron:", err);
    }
  };

  const handleAttachmentDownload = async (attachmentId, filename) => {
    try {
      const response = await emailAPI.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download attachment:", err);
    }
  };

  const handleRowClick = (email) => {
    if (selectedEmail?.id === email.id) {
      setSelectedEmail(null);
    } else {
      setSelectedEmail(email);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const handleOpenTaskDialog = (email) => {
    setTaskDialogData({
      title: email.subject,
      description: "",
      status: "todo",
      priority: "medium",
      email_message_id: email.id,
    });
    setTaskDialogOpen(true);
  };

  const handleOpenEventDialog = (email) => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later
    setEventDialogData({
      title: email.subject,
      description: "",
      start: now,
      end: end,
      invitees: "",
      notifications: [{ days: 0, hours: 1, minutes: 0 }],
      email_message_id: email.id,
    });
    setEventDialogOpen(true);
  };

  const handleOpenAssignTaskDialog = (email) => {
    setSelectedEmail(email);
    setAssignTaskDialogOpen(true);
    setSelectedTaskToAssign(null);
  };

  const handleAssignTask = async () => {
    if (!selectedTaskToAssign || !selectedEmail) return;

    try {
      // We need to fetch the task to get existing email IDs to avoid overwriting them
      // Assuming tasksAPI.getTask exists. If not, we might need to rely on the loaded task object if it has email_ids or emails.
      // But the Task list object might be a summary.
      // However, the backend update logic usually requires passing the full list if we are using the 'email_ids' field on TaskUpdate.
      // BUT: The backend endpoint code we saw (tasks.py) says:
      // if task_in.email_ids is not None: for email_id in ... link_email_to_task
      // It iterates and calls link_email_to_task. It does NOT seem to replace the list (delete others).
      // It just links the provided ones.
      // Let's verify that assumption.
      // backend/app/api/v1/endpoints/tasks.py:
      // if task_in.email_ids is not None:
      //    for email_id in task_in.email_ids:
      //        email_service.link_email_to_task(...)
      // valid assumption: it ADDS links. It does not unlink.
      // So we can just send [selectedEmail.id].

      await tasksAPI.updateTask(selectedTaskToAssign.id, {
        email_ids: [selectedEmail.id],
      });

      // Update local state
      setEmails((prev) =>
        prev.map((e) =>
          e.id === selectedEmail.id
            ? { ...e, task_id: selectedTaskToAssign.id }
            : e,
        ),
      );

      setAssignTaskDialogOpen(false);
      setSelectedTaskToAssign(null);
    } catch (e) {
      console.error("Failed to assign task", e);
    }
  };

  const handleCloseDialogs = () => {
    setTaskDialogOpen(false);
    setEventDialogOpen(false);
    setAssignTaskDialogOpen(false);
    setTaskDialogData(null);
    setEventDialogData(null);
  };

  const handleSaveTask = async (taskData, files) => {
    try {
      const payload = {
        ...taskData,
        description:
          taskData.description +
          `\n\nIs based on Email-ID: ${selectedEmail.id}`,
        email_ids: [selectedEmail.id],
      };
      const response = await tasksAPI.createTask(payload);

      if (files && files.length > 0) {
        try {
          const formData = new FormData();
          files.forEach((file) => formData.append("files", file));
          await tasksAPI.uploadAttachments(response.data.id, formData);
        } catch (uploadErr) {
          console.error("Fehler beim Hochladen der Anhänge:", uploadErr);
        }
      }

      // Update local state to reflect the new task linkage
      setEmails((prev) =>
        prev.map((e) =>
          e.id === selectedEmail.id
            ? { ...e, task_id: response.data?.id || true }
            : e,
        ),
      );

      handleCloseDialogs();
      // Optionally show success message
    } catch (error) {
      console.error("Failed to create task:", error);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      // Resolve emails from usernames for EventDialog if needed,
      // BUT EventDialog in Calendar/index.jsx handles this conversion (resolving usernames to emails).
      // Here in EmailTab, we are using EventDialog.
      // EventDialog returns 'invitees' as a string of "Name1, Name2".
      // We need to convert these names back to emails if backend expects emails.
      // CalendarPage does this in its handleSave. We should replicate that logic or reuse it.

      const inviteeNames = eventData.invitees
        ? eventData.invitees
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
        : [];

      const attendeeEmails = inviteeNames.map((name) => {
        const user = users.find((u) => u.username === name || u.email === name);
        return user ? user.email : name;
      });

      const payload = {
        title: eventData.title,
        description:
          eventData.description +
          `\n\nIs based on Email-ID: ${selectedEmail.id}`,
        start_time: eventData.start.toISOString(),
        end_time: eventData.end.toISOString(),
        attendee_emails: attendeeEmails,
        notifications: eventData.notifications,
        email_message_id: selectedEmail.id,
      };

      const response = await calendarAPI.createCalendarEvent(payload);

      // Update local state to reflect the new calendar event linkage
      setEmails((prev) =>
        prev.map((e) =>
          e.id === selectedEmail.id
            ? { ...e, calendar_event_id: response.data?.id || true }
            : e,
        ),
      );

      handleCloseDialogs();
    } catch (error) {
      console.error("Failed to create event:", error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (!deleteTargetEmailId) return;
      await emailAPI.deleteEmailMessage(deleteTargetEmailId);
      if (selectedEmail?.id === deleteTargetEmailId) setSelectedEmail(null);
      setDeleteDialogOpen(false);
      setDeleteTargetEmailId(null);
      loadEmails();
    } catch (err) {
      console.error("Failed to delete email:", err);
    }
  };

  const handleSubmitDelete = async (e) => {
    e.preventDefault();
    await handleConfirmDelete();
  };

  return (
    <Box p={3}>
      {/* Cron Box */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" mb={1}>
          Zeitplan-Einstellungen
        </Typography>
        <Box
          mb={2}
          sx={{
            "& .cron_builder": {
              width: "50%",
              minWidth: "50%",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              p: 2,
            },
          }}
        >
          <Typography variant="subtitle1" mb={1}>
            Zeitplan festlegen:
          </Typography>
          <Cron
            onChange={(e) => setCronValue(e)}
            Cron-Einstellungen
            value={cronValue}
            showResultText={true}
            showResultCron={true}
            locale="de"
            translateFn={translateFn}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button variant="outlined" onClick={startCron}>
            Cron starten
          </Button>
          <Button variant="outlined" onClick={stopCron}>
            Cron stoppen
          </Button>
          <Button variant="contained" onClick={fetchEmails} disabled={loading}>
            Von IMAP Server laden
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                checked={markAsSeen}
                onChange={(e) => setMarkAsSeen(e.target.checked)}
              />
            }
            label="Als gelesen markieren"
          />
          {loading && <CircularProgress size={20} />}
        </Box>
        {cronStatus && (
          <Box mt={1}>
            <Typography variant="body2">
              Cron Status: {cronStatus.active ? "Läuft" : "Gestoppt"}
            </Typography>
            {cronStatus.next_run_at && (
              <Typography variant="body2">
                Nächster Lauf:{" "}
                {cronStatus.active
                  ? new Date(cronStatus.next_run_at).toLocaleString()
                  : "N/A"}
              </Typography>
            )}
            {cronStatus.last_run_at && (
              <Typography variant="body2">
                Letzter Lauf:{" "}
                {new Date(cronStatus.last_run_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Search & Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Suche Absender"
              variant="outlined"
              fullWidth
              size="small"
              value={searchSender}
              onChange={(e) => setSearchSender(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Suche Betreff"
              variant="outlined"
              fullWidth
              size="small"
              value={searchSubject}
              onChange={(e) => setSearchSubject(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
              >
                Suchen
              </Button>
              <Button
                variant="outlined"
                onClick={() => loadEmails()}
                disabled={loading}
              >
                Aktualisieren
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Emails Table */}
      <TableContainer component={Paper}>
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "18%" }}>Absender</TableCell>
              <TableCell sx={{ width: "56%" }}>Betreff</TableCell>
              <TableCell sx={{ width: "18%" }}>Datum</TableCell>
              <TableCell sx={{ width: "8%" }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.map((email) => (
              <React.Fragment key={email.id}>
                <TableRow
                  onClick={() => handleRowClick(email)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.100" },
                    bgcolor:
                      selectedEmail?.id === email.id
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <TableCell>{email.sender}</TableCell>
                  <TableCell>{email.subject}</TableCell>
                  <TableCell>{new Date(email.date).toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="löschen"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTargetEmailId(email.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={4}
                  >
                    <Collapse
                      in={selectedEmail?.id === email.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ ml: 2, mr: 2, mb: 2, mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          {/* Email Content Column */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom>
                              Inhalt:
                            </Typography>
                            <Box
                              sx={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "4px",
                                p: 2,
                                overflow: "auto",
                                resize: "both",
                                width: "100%",
                                height: "800px",
                                boxSizing: "border-box",
                                whiteSpace: email.html_body
                                  ? "normal"
                                  : "pre-wrap",
                                wordWrap: "break-word",
                                "&:hover": { boxShadow: 3 }, // Interaction
                                "& img": { maxWidth: "100%" }, // Target nested HTML
                              }}
                              dangerouslySetInnerHTML={{
                                __html: email.html_body || email.body || "",
                              }}
                            />
                            {email.attachments &&
                              email.attachments.length > 0 && (
                                <Box mt={2}>
                                  <Typography variant="subtitle1">
                                    Anhänge:
                                  </Typography>
                                  {email.attachments.map((att, idx) => (
                                    <Typography
                                      key={idx}
                                      component="span"
                                      onClick={() =>
                                        handleAttachmentDownload(
                                          att.id,
                                          att.filename,
                                        )
                                      }
                                      style={{
                                        color: "blue",
                                        display: "block",
                                        marginTop: "4px",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      {att.filename}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                          </Grid>

                          {/* Actions Column */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom>
                              Aktionen:
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={2}>
                              {email.task_id ? (
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  fullWidth
                                  href={`/dashboard?task=${email.task_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Zur Aufgabe
                                </Button>
                              ) : (
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                >
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    onClick={() => handleOpenTaskDialog(email)}
                                  >
                                    Aufgabe erstellen
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    onClick={() =>
                                      handleOpenAssignTaskDialog(email)
                                    }
                                  >
                                    Zu bestehender Aufgabe zuweisen
                                  </Button>
                                </Box>
                              )}
                              {email.calendar_event_id ? (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  fullWidth
                                  href="/calendar"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Zum Kalendereintrag
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  fullWidth
                                  onClick={() => handleOpenEventDialog(email)}
                                >
                                  Kalendereintrag erstellen
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {emails.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Keine E-Mails gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </TableContainer>

      {/* Dialogs */}
      {taskDialogOpen && (
        <TaskDialog
          open={taskDialogOpen}
          onClose={handleCloseDialogs}
          task={taskDialogData}
          users={users}
          currentUser={currentUser}
          onSave={handleSaveTask}
        />
      )}

      {eventDialogOpen && (
        <EventDialog
          open={eventDialogOpen}
          onClose={handleCloseDialogs}
          initialData={eventDialogData}
          availableUsernames={availableUsernames}
          onSave={handleSaveEvent}
          isEditMode={false}
        />
      )}
      {/* Assign Task Dialog */}
      <Dialog
        open={assignTaskDialogOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>E-Mail zu bestehender Aufgabe zuweisen</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Autocomplete
              options={tasks}
              getOptionLabel={(option) =>
                `${option.title} (ID: ${option.id})` || `Aufgabe ${option.id}`
              }
              value={selectedTaskToAssign}
              onChange={(event, newValue) => setSelectedTaskToAssign(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Aufgabe suchen"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Abbrechen</Button>
          <Button
            onClick={handleAssignTask}
            variant="contained"
            color="primary"
            disabled={!selectedTaskToAssign}
          >
            Zuweisen
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <form onSubmit={handleSubmitDelete}>
          <DialogTitle id="delete-dialog-title">Löschen bestätigen</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Sind Sie sicher, dass Sie diese E-Mail und alle zugehörigen
              Anhänge dauerhaft löschen möchten?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" color="error" variant="contained">
              Löschen
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EmailTab;
